import { useState, useRef, useEffect } from "react";
import { Globe, ChevronDown, Check } from "lucide-react";
import { useLocale, CURRENCIES } from "@/context/LocaleContext";

/**
 * Compact language + currency switcher for the footer.
 *
 * Persists user choice in localStorage (handled by LocaleContext). All copy on
 * the site reacts through `tr()` / `fmtPrice()` — this component just exposes
 * the toggle UI that was previously missing.
 */
const LANG_OPTIONS = [
    { code: "pt", label: "Português", short: "PT" },
    { code: "en", label: "English", short: "EN" },
];

const CURRENCY_OPTIONS = Object.keys(CURRENCIES).map((code) => ({
    code,
    label: `${code} · ${CURRENCIES[code].symbol}`,
    short: code,
}));

function Dropdown({ testId, label, options, currentCode, onSelect, ariaLabel }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const current = options.find((o) => o.code === currentCode) || options[0];

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid={testId}
                aria-label={ariaLabel}
                aria-haspopup="listbox"
                aria-expanded={open}
                className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1.5 text-xs font-bold text-white hover:bg-white/10 hover:border-secondary/40 transition backdrop-blur"
            >
                {label ? <label.icon className="h-3.5 w-3.5 text-secondary" aria-hidden /> : null}
                <span>{current.short}</span>
                <ChevronDown className={"h-3.5 w-3.5 text-white/60 transition " + (open ? "rotate-180" : "")} aria-hidden />
            </button>
            {open && (
                <ul
                    role="listbox"
                    aria-label={ariaLabel}
                    className="absolute bottom-full left-0 mb-2 min-w-[170px] rounded-xl border border-white/15 bg-primary/95 backdrop-blur-lg shadow-2xl py-1 z-40"
                >
                    {options.map((opt) => {
                        const active = opt.code === currentCode;
                        return (
                            <li key={opt.code}>
                                <button
                                    type="button"
                                    onClick={() => { onSelect(opt.code); setOpen(false); }}
                                    data-testid={`${testId}-option-${opt.code}`}
                                    role="option"
                                    aria-selected={active}
                                    className={
                                        "w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition " +
                                        (active
                                            ? "text-secondary font-bold"
                                            : "text-white/80 hover:bg-white/10 hover:text-white")
                                    }
                                >
                                    <span className="flex-1">{opt.label}</span>
                                    {active && <Check className="h-3.5 w-3.5 text-secondary flex-shrink-0" aria-hidden />}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

export default function LocaleSwitch() {
    const { lang, setLang, currency, setCurrency } = useLocale();

    return (
        <div className="inline-flex items-center gap-2" data-testid="locale-switch">
            <Dropdown
                testId="locale-switch-lang"
                label={{ icon: Globe }}
                options={LANG_OPTIONS}
                currentCode={lang}
                onSelect={setLang}
                ariaLabel="Escolher idioma"
            />
            <Dropdown
                testId="locale-switch-currency"
                label={null}
                options={CURRENCY_OPTIONS}
                currentCode={currency}
                onSelect={setCurrency}
                ariaLabel="Escolher moeda"
            />
        </div>
    );
}
